import DatePicker from "@/components/date-picker";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { RequestTypes } from "@/constants/entity-status";
import { EntityTypes } from "@/constants/enums";
import { useAuth } from "@/hooks/useAuth";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/requests/check/schema/handler-functions";
import { getSucursales, Sucursal } from "@/services/accounts.service";
import {
  EntityStatusOption,
  getStatusByEntity,
} from "@/services/entity-status.service";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { Filter, SearchIcon } from "lucide-react";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Control,
  FieldValues,
  UseFormHandleSubmit,
  useWatch,
} from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  loadingOptions?: boolean;
}

export const CheckRequestFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  control,
  loadingOptions,
  submitFilters,
  handleSubmit,
  setOpenFilters,
  //handleSearch,
  clearFilters,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();

  const [loadingSucursales, setLoadingSucursales] = useState<boolean>(false);

  const [statusOptions, setStatusOptions] = useState<EntityStatusOption[]>([]);
  const [loadingStatusOptions, setLoadingStatusOptions] =
    useState<boolean>(false);

  const selectedRequestType = useWatch({
    control,
    name: "request_type",
  }) as string[] | undefined;

  const resolveStatusIdsForRequestType = (
    ids: number[],
    requestType?: string,
  ): number[] => {
    if (!requestType) return ids;

    if (requestType === String(RequestTypes.PHYSICAL_CHECK)) {
      return typeof ids?.[0] === "number" ? [ids[0]] : [];
    }

    if (requestType === String(RequestTypes.E_CHEQ)) {
      return typeof ids?.[1] === "number" ? [ids[1]] : [];
    }

    return ids;
  };

  const statusSelectItems = useMemo(() => {
    return statusOptions?.map((status) => {
      const resolvedIds = resolveStatusIdsForRequestType(
        status?.ids ?? [],
        selectedRequestType?.[0],
      );

      return {
        label: status?.nombre,
        value: resolvedIds.join("|"),
      };
    });
  }, [statusOptions, selectedRequestType]);

  const fetchStatusOptions = async () => {
    setLoadingStatusOptions(true);
    const response = await getStatusByEntity({
      Id: [EntityTypes.SolicitudCheque, EntityTypes.SolicitudECheq],
    });

    setStatusOptions(response ?? []);
    setLoadingStatusOptions(false);
  };

  const [sucursales, setSucursales] = useState<Sucursal[] | null>([]);

  const fetchSucursales = async () => {
    setLoadingSucursales(true);

    const response = await getSucursales();
    setSucursales(response);

    setLoadingSucursales(false);
  };

  useEffect(() => {
    if (isActive && !isEmpty(user?.localAccountId)) {
      fetchSucursales();
      fetchStatusOptions();
    }
  }, [isActive, user?.localAccountId]);

  const getOptions = async (
    inputValue: string,
  ): Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  > => {
    return getOptionsHandler({
      inputValue,
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions,
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });
  };

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems={"center"} justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <DatePicker
                    control={control}
                    labelText="Fecha"
                    name="request_date"
                    placeholder="Ingresar fecha"
                    maxDate={dayjs()}
                  />
                </GridItem>
                <GridItem maxWidth="250px">
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Titular cuenta</Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadOptions}
                      name="account_number"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={
                        disableFilters || loadingFilters || loadingAccounts
                      }
                      closeMenuOnSelect
                      isClearable
                      triggerWithLength
                    />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="sucursal"
                    items={
                      sucursales?.map((status) => ({
                        label: status?.nombre,
                        value: status?.clave,
                      })) ?? []
                    }
                    labelText="Sucursal"
                    placeholder="Seleccionar una opción"
                    control={control}
                    loading={loadingSucursales}
                    disabled={loadingSucursales}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
                <GridItem>
                  <Field.Root>
                    <Field.Label>Monto</Field.Label>
                    <InputCurrency
                      name="request_amount"
                      placeholder="Ingresar monto"
                      control={control}
                      maxLength={20}
                      disabled={disableFilters || loadingFilters}
                    />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <InputField
                    inputName="requester"
                    labelText="Solicitante"
                    placeholder="Ingresar solicitante"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="request_type"
                    items={[
                      {
                        label: "Cheque Físico",
                        value: RequestTypes.PHYSICAL_CHECK,
                      },
                      {
                        label: "E-Cheq",
                        value: RequestTypes.E_CHEQ,
                      },
                    ]?.map((status) => ({
                      label: status?.label,
                      value: status?.value,
                    }))}
                    labelText="Tipo de cheque"
                    placeholder="Seleccionar una opción"
                    control={control}
                    loading={loadingOptions}
                  />
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="estados"
                    items={statusSelectItems}
                    labelText="Estado"
                    placeholder="Seleccionar una opción"
                    control={control}
                    loading={loadingOptions || loadingStatusOptions}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters}
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
